<script setup lang="ts">
import { onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useCartStore } from '@/Stores/cart.store'
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { Link } from '@inertiajs/vue3'
import Button from '@/Components/Button.vue'

const store = useCartStore()
const { t, n } = useI18n()

onUnmounted(() => {
    store.show = false
})
</script>

<template>
    <TransitionRoot as="template" :show="store.show">
        <Dialog as="div" class="relative z-50" aria-labelledby="cart-title" role="dialog" aria-modal="true" @close="store.show = false">
            <TransitionChild as="template" enter="ease-in-out duration-500" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in-out duration-500" leave-from="opacity-100" leave-to="opacity-0">
                <div class="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />
            </TransitionChild>

            <div class="fixed inset-0 overflow-hidden">
                <div class="absolute inset-0 overflow-hidden">
                    <div class="fixed inset-y-0 right-0 flex max-w-full pointer-events-none sm:pl-10">
                        <TransitionChild as="template" enter="transform transition ease-in-out duration-500 sm:duration-700" enter-from="translate-x-full" enter-to="translate-x-0" leave="transform transition ease-in-out duration-500 sm:duration-700" leave-from="translate-x-0" leave-to="translate-x-full">
                            <DialogPanel class="w-screen max-w-md pointer-events-auto">
                                <div class="flex flex-col h-full overflow-y-scroll bg-white shadow-xl">
                                    <div class="flex flex-col flex-1 px-4 py-6 overflow-y-auto sm:px-6">
                                        <div class="flex items-start justify-between">
                                            <DialogTitle id="cart-title" class="text-lg font-medium text-gray-900">{{ t('components.cart.your-cart') }}</DialogTitle>

                                            <div class="flex items-center ml-3 h-7">
                                                <button type="button" class="inline-flex items-center justify-center p-2 -m-2 text-gray-400 rounded-md hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500" @click="store.show = false">
                                                    <span class="sr-only">Close cart</span>
                                                    <XMarkIcon class="w-6 h-6 stroke-2" aria-hidden="true" />
                                                </button>
                                            </div>
                                        </div>

                                        <div v-if="store.items.length === 0" class="my-auto text-center">
                                            <p class="text-lg font-medium text-gray-900">{{ t('components.cart.cart-empty') }}</p>
                                            <button type="button" class="mt-6 text-sm font-medium text-indigo-600 hover:text-indigo-500" @click="store.show = false">{{ t('components.cart.continue-shopping') }}<span aria-hidden="true"> &rarr;</span></button>
                                        </div>

                                        <div v-if="store.items.length > 0" class="mt-8">
                                            <div class="flow-root">
                                                <ul role="list" class="-my-6">
                                                    <li v-for="(item, itemIdx) in store.items" :key="item.id" class="flex py-6" :class="[itemIdx !== 0 ? 'border-t border-t-gray-200' : '']">
                                                        <div class="flex-shrink-0 w-24 h-24 overflow-hidden rounded-md">
                                                            <img :src="item.product.image_url" :alt="item.product.title">
                                                        </div>

                                                        <div class="flex flex-col flex-1 ml-4">
                                                            <div>
                                                                <div class="flex justify-between text-sm font-medium text-gray-900">
                                                                    <a :href="item.product.product_url">{{ item.product.title }}</a>
                                                                    <div class="ml-4">
                                                                        <p class="whitespace-nowrap" :class="{ 'line-through': item.product.sale_price }">{{ n(item.product.retail_price, 'currency') }}</p>
                                                                        <p v-if="item.product.sale_price" class="text-red-500 whitespace-nowrap">{{ n(item.product.sale_price, 'currency') }}</p>
                                                                    </div>
                                                                </div>

                                                                <p v-if="item.product.variant" class="mt-1 text-sm text-gray-500">{{ item.product.variant }}</p>
                                                            </div>

                                                            <div class="flex items-center justify-between flex-1 mt-2 text-sm">
                                                                <select v-if="item.product.maximum_quantity > 0" class="block max-w-full rounded-md border border-gray-300 py-1.5 text-base leading-5 font-medium text-gray-700 text-left shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" @change="store.updateQuantity(item.id, parseInt(($event.target as HTMLSelectElement).value) || 1)">
                                                                    <option v-for="i in item.product.maximum_quantity" :key="i" :value="i" :selected="i === item.quantity">{{ i }}</option>
                                                                </select>

                                                                <p v-if="item.product.maximum_quantity === 0" class="text-gray-500">{{ t('Out of Stock') }}</p>

                                                                <div class="flex">
                                                                    <button type="button" class="font-medium text-indigo-600 hover:text-indigo-500" @click="store.deleteItem(item.id)">{{ t('components.cart.remove') }}</button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>

                                    <div v-if="store.items.length > 0" class="px-4 py-6 border-t border-gray-200 sm:px-6">
                                        <div class="flex justify-between text-base font-medium text-gray-900">
                                            <p>{{ t('components.cart.subtotal') }}</p>
                                            <p>{{ $n(store.subtotal, 'currency') }}</p>
                                        </div>

                                        <div v-if="store.saveAmount > 0" class="flex justify-between text-base text-red-500">
                                            <p>{{ t('components.cart.you-save') }}</p>
                                            <p>{{ $n(store.saveAmount, 'currency') }}</p>
                                        </div>

                                        <!-- <p class="mt-0.5 text-sm text-gray-500">Shipping and taxes calculated at checkout.</p> -->
                                        <div class="mt-6">
                                            <Button :as="Link" size="xl" :href="route('checkout')" class="w-full">
                                                {{ t('components.cart.continue-checkout') }}
                                            </Button>
                                        </div>

                                        <div class="flex justify-center mt-4 text-xs text-center text-gray-500 sm:mt-6 sm:text-sm">
                                            <p>
                                                {{ t('components.cart.or') }} <button type="button" class="font-medium text-indigo-600 hover:text-indigo-500" @click="store.show = false">{{ t('components.cart.continue-shopping') }}<span aria-hidden="true"> &rarr;</span></button>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>
